/**
 * cargasApi — chamadas à API de Cargas (M2) sobre o ApiClient base.
 * Respostas seguem o envelope do contrato (`{ data }` / `{ data, nextCursor }`).
 */

import { ApiClient } from './apiClient';
import {
  CargaDetalhe,
  CargaListItem,
  CriarCargaResponse,
  ManifestoItem,
  RenovarUrlsResponse,
} from './cargas-types';

interface Envelope<T> {
  readonly data: T;
}
interface EnvelopeLista<T> {
  readonly data: readonly T[];
  readonly nextCursor: string | null;
}

export class CargasApi {
  constructor(private readonly client: ApiClient) {}

  /** POST /cargas — cria a carga e retorna as instruções de upload. */
  async criar(origem: 'browser' | 'script', manifesto: readonly ManifestoItem[]): Promise<CriarCargaResponse> {
    const resp = await this.client.request<Envelope<CriarCargaResponse>>('/cargas', {
      method: 'POST',
      body: JSON.stringify({ origem, manifesto }),
    });
    return resp.data;
  }

  /** POST /cargas/{id}/upload-urls — URLs novas para os arquivos que faltam (US-08 AC4). */
  async renovarUrls(cargaId: string, manifesto: readonly ManifestoItem[]): Promise<RenovarUrlsResponse> {
    const resp = await this.client.request<Envelope<RenovarUrlsResponse>>(
      `/cargas/${encodeURIComponent(cargaId)}/upload-urls`,
      { method: 'POST', body: JSON.stringify({ manifesto }) },
    );
    return resp.data;
  }

  /** GET /cargas — lista paginada. */
  async listar(cursor?: string, limit = 20): Promise<{ itens: readonly CargaListItem[]; nextCursor: string | null }> {
    const query = new URLSearchParams();
    query.set('limit', String(limit));
    if (cursor) {
      query.set('cursor', cursor);
    }
    const resp = await this.client.request<EnvelopeLista<CargaListItem>>(`/cargas?${query.toString()}`);
    return { itens: resp.data, nextCursor: resp.nextCursor };
  }

  /** GET /cargas/{id} — detalhe + relatório. */
  async detalhe(id: string): Promise<CargaDetalhe> {
    const resp = await this.client.request<Envelope<CargaDetalhe>>(`/cargas/${encodeURIComponent(id)}`);
    return resp.data;
  }

  /** URL de download do script Python (admin-only na API). */
  scriptDownloadUrl(baseUrl: string): string {
    return `${baseUrl}/cargas/script/download`;
  }
}
